import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * na-quick-add
 *
 * Lightweight quick-add for the Product Grid section.
 *
 * Cart integration follows the theme's own product-form contract:
 *   1. POST /cart/add.js requesting the drawer section ('cart-drawer-section')
 *      via the `sections` param so the response carries rendered drawer HTML.
 *   2. Dispatch CartLinesUpdateEvent with a deferred `promise`.
 *   3. Resolve that promise with { cart, detail: { sections, items, ... } }.
 *
 * The cart-drawer-component (auto-open) and cart-items-component both listen for
 * this event: the drawer opens and re-renders from detail.sections. If sections
 * are absent, cart-items falls back to fetching them itself, so this stays
 * robust either way.
 *
 * Sold-out sizes are not rendered (handled in Liquid), so every chip is buyable.
 */

(function () {
  if (window.__naQuickAddInit) return;
  window.__naQuickAddInit = true;

  const OPEN_CLASS = 'is-open';
  // The theme renders the drawer's cart-items-component with this fixed id.
  const DRAWER_SECTION_ID = 'cart-drawer-section';

  function closeOthers(except) {
    document.querySelectorAll('[data-na-card].' + OPEN_CLASS).forEach((card) => {
      if (card === except) return;
      card.classList.remove(OPEN_CLASS);
      const toggle = card.querySelector('[data-na-quick-add-toggle]');
      const tray = card.querySelector('[data-na-sizes]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      if (tray) tray.hidden = true;
    });
  }

  /**
   * Open the cart drawer directly via its <theme-drawer> host. Waits briefly for
   * the custom element to upgrade so open() is available, and no-ops on the cart
   * page (where there is no drawer).
   */
  function openCartDrawer() {
    const drawer =
      document.querySelector('cart-drawer-component')?.closest('theme-drawer') ||
      document.getElementById('cart-drawer');

    if (!drawer) return;

    const tryOpen = () => {
      if (typeof drawer.open === 'function') {
        if (!drawer.isOpen) drawer.open();
        return true;
      }
      return false;
    };

    if (tryOpen()) return;

    // Element not upgraded yet — upgrade and retry on the next frame.
    if (window.customElements) customElements.upgrade(drawer);
    requestAnimationFrame(() => {
      tryOpen();
    });
  }

  function collapse(card) {
    if (!card) return;
    card.classList.remove(OPEN_CLASS);
    const toggle = card.querySelector('[data-na-quick-add-toggle]');
    const tray = card.querySelector('[data-na-sizes]');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    if (tray) tray.hidden = true;
  }

  /**
   * @param {HTMLButtonElement} chip
   */
  async function addToCart(chip) {
    const variantId = chip.dataset.variantId;
    if (!variantId || chip.dataset.loading === 'true') return;

    chip.dataset.loading = 'true';
    chip.classList.add('is-loading');

    // Clean /cart/add.js payload. We request the drawer section so the response
    // includes its rendered HTML - but we DO NOT send sections_url (sending the
    // current page path there was what caused the 422 / red state).
    const body = {
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: DRAWER_SECTION_ID,
    };

    const deferred = CartLinesUpdateEvent.createPromise();

    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      // Shopify returns { status, message, description } on failure.
      if (!res.ok || data.status) {
        chip.classList.add('is-error');
        setTimeout(() => chip.classList.remove('is-error'), 1500);
        return;
      }

      // Dispatch the theme cart event with a deferred promise, then resolve it
      // with the returned sections so the drawer morphs its contents. The event
      // itself triggers the cart-drawer auto-open.
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }],
          promise: deferred.promise,
        })
      );

      // Fetch the authoritative cart state to resolve the promise with, matching
      // the shape product-form uses.
      let ajaxCart = null;
      try {
        const cartRes = await fetch(`${Theme.routes.cart_url}.json`, {
          headers: { Accept: 'application/json' },
        });
        if (cartRes.ok) ajaxCart = await cartRes.json();
      } catch (e) {
        /* non-fatal: drawer will self-fetch sections if needed */
      }

      deferred.resolve({
        cart: ajaxCart ? CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart) : undefined,
        detail: {
          items: ajaxCart ? ajaxCart.items : undefined,
          source: 'na-quick-add',
          sourceId: 'na-quick-add',
          itemCount: 1,
          sections: data.sections,
          didError: false,
        },
      });

      // Explicitly open the cart drawer. The cart-drawer-component only
      // auto-opens when the merchant has enabled the "auto open cart drawer"
      // theme setting (it gates on an `auto-open` attribute), and it also skips
      // its synchronous open path when the cart was previously empty. Opening
      // here makes quick-add reliably open the drawer regardless. The drawer's
      // own open() is idempotent, so this is safe even when auto-open already
      // fired.
      openCartDrawer();

      collapse(chip.closest('[data-na-card]'));
    } catch (err) {
      console.warn('[na-quick-add] add to cart failed:', err);
      deferred.reject(err);
      chip.classList.add('is-error');
      setTimeout(() => chip.classList.remove('is-error'), 1500);
    } finally {
      chip.dataset.loading = 'false';
      chip.classList.remove('is-loading');
    }
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const toggle = target.closest('[data-na-quick-add-toggle]');
    if (toggle) {
      event.preventDefault();
      const card = toggle.closest('[data-na-card]');
      if (!card) return;
      const tray = card.querySelector('[data-na-sizes]');
      const willOpen = !card.classList.contains(OPEN_CLASS);
      closeOthers(card);
      card.classList.toggle(OPEN_CLASS, willOpen);
      toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      if (tray) tray.hidden = !willOpen;
      return;
    }

    const chip = target.closest('[data-na-size]');
    if (chip instanceof HTMLButtonElement) {
      event.preventDefault();
      addToCart(chip);
      return;
    }

    if (!target.closest('[data-na-card]')) {
      closeOthers(null);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeOthers(null);
  });
})();